import { useEffect, useRef, useState } from "react";
import ImageSection from "./listing/ImageSection";
import ListingNoAvailable from "./listing/ListingNoAvaliable";
import VehicleDetailSection from "./listing/VehicleDetailSection";
import PersonalDetailSection from "./listing/PersonalDetailSection";
import {
  LISTING_VEHICLES_LIMIT,
  MAX_MILES,
  MAX_PRICE,
  MAX_YEAR,
  MIN_MILES,
  MIN_PRICE,
  MIN_YEAR,
  initFilters,
} from "@/libs/constants";
import { isMobileDevice, setScrollHidden } from "@/libs/utils";
import GoogleMapReact from "google-map-react";
const GoogleMap = GoogleMapReact as any;
import ContentBlock from "./ContentBlock";
import MarkerIcon from "./Icon/MarkerIcon";
import LinkHub from "./LinkHub";
import AdditionalServices from "./listing/AdditionalServices";
import LoanCalculator from "./listing/LoanCalculator";
import OtherVehiclesSection from "./listing/OtherVehiclesSection";
import VehicleTransactionInfoSection from "./listing/VehicleTransactionInfoSection";
import { useAuthValue } from "@/contexts/contextAuth";
import { getBanner, getSnippets } from "@/cms/strapi";
import { LISTING_DETAILS_SOLD_CONTENT } from "@/cms/components/listing-details-sold";
import { Props } from "@/pages/listing/[slug]";
import { GetStaticPaths } from "next";
import { googleMapsApiKey } from "@/libs/clientConfig";
import { paServerInventoryBuild, serverApi } from "@/libs/serverApi";
import VideoSection from "./listing/VideoSection";

const isLoanCalcShown = (listing: any) => {
  const { RegistrationYear, Mileage, TitleType } = listing;
  const acceptableYear = new Date().getFullYear() - 6;
  return (
    RegistrationYear >= acceptableYear &&
    Mileage < 125000 &&
    TitleType === "Clear"
  );
};

const ListingPage = ({
  content,
  vehicleData,
  similarData,
  extraDataForSold,
}: Props) => {
  const [isMobile, setIsMobile] = useState<boolean>(false);
  useEffect(() => {
    setIsMobile(isMobileDevice());
  }, []);

  const [disclaimer, setDisclaimer] = useState<string>("");

  const { setIsSignup, setSource, setIsListingAction, setRedirectUrl } =
    useAuthValue();
  const data: any = content;

  const defaultProps = {
    center: {
      lat: 10.99835602,
      lng: 77.01502627,
    },
    zoom: 17,
  };

  // Loan Calculator
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const loanCalculatorRef = useRef<HTMLDivElement | null>(null);
  const isLoanable = isLoanCalcShown(data);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window?.dataLayer &&
        window.dataLayer.push({
          event: "Listing View",
        });
    }

    setDisclaimer(
      data?.originalSourceInfo
        ? "PrivateAuto, PrivateAuto.com, and DealNow are registered trademarks owned by Private Auto Inc. Private Auto Inc. does not have any affiliation, sponsorship, or endorsement agreements with the companies mentioned, depicted, or promoted on its website. The vehicle information provided is supplied by the seller and may include data from third parties; PrivateAuto is not responsible for the accuracy of such information. PrivateAuto provides this service and materials without representations or warranties of any kind, either expressed or implied. Company logos or trademarks used do not imply endorsement and are the property of their respective owners."
        : `The vehicle information is supplied by the seller and may include
    data from third parties; PrivateAuto is not responsible for the
    accuracy of such information. PrivateAuto provides this service and
    materials without representations or warranties of any kind, either
    expressed or implied.`
    );
  }, []);

  return (
    <>
      {data?.status == "deactivated" ||
      data?.status == "pending" ||
      data?.status === "expired" ||
      data?.status === "expiredGrace" ||
      data?.displayType == "private" ? (
        <ListingNoAvailable similarData={similarData.data} slug={data?.slug} />
      ) : (
        <main className="w-full md:max-w-[1000px] mx-auto">
          <ImageSection data={data} slug={data?.slug} />
          <VehicleDetailSection
            data={data}
            isLoanable={isLoanable}
            monthlyPayment={monthlyPayment}
            onMonthlyPaymentClick={() => {
              if (loanCalculatorRef.current) {
                loanCalculatorRef.current.scrollIntoView({
                  behavior: "smooth",
                });
              }
            }}
          />

          {data.uploadVideos?.length > 0 && (
            <>
              <hr className="w-full hr-thick my-6" />
              <VideoSection videos={data?.uploadVideos} />
            </>
          )}
          {!data?.originalSourceInfo && (
            <>
              <hr className="w-full hr-thick my-6" />
              <PersonalDetailSection data={data} />
            </>
          )}

          <AdditionalServices vehicleData={data} />
          <div className="w-full px-4 md:px-0">
            <hr className="hr-thick mt-10" />
          </div>
          {data &&
          data.testDriveLocation != undefined &&
          Object.keys(data.testDriveLocation).length != 0 ? (
            <section className="w-full md:w-[820px] px-4 md:px-0 my-8">
              <div className="flex justify-between">
                <div className="space-y-2">
                  <p className="text-xl sm:text-3xl font-bold">
                    Test drive location
                  </p>
                  {data.listingLocation != undefined ? (
                    <p className="text-sm md:text-lg text-[#212529]">
                      {data.listingLocation.city},&nbsp;
                      {data.listingLocation.stateShortname}
                    </p>
                  ) : (
                    ""
                  )}
                </div>
                {data?.status != "sold" && (
                  <div className="flex items-center">
                    <button
                      className="btn-primary"
                      onClick={() => {
                        setSource("Schedule Test Drive");
                        setIsSignup(true, {
                          isListingAction: `${data.slug}?action=scheduleTestDrive`,
                        });
                        setScrollHidden();
                        setIsListingAction(
                          `${data.slug}?action=scheduleTestDrive`
                        );
                      }}
                    >
                      {isMobile ? "Schedule" : "Schedule Test Drive"}
                    </button>
                  </div>
                )}
              </div>
              <div className="w-full h-[216px] my-6">
                <GoogleMap
                  bootstrapURLKeys={{
                    key: googleMapsApiKey,
                  }}
                  defaultCenter={{
                    lat: data.testDriveLocation.geometry.latitude,
                    lng: data.testDriveLocation.geometry.longitude,
                  }}
                  defaultZoom={defaultProps.zoom}
                >
                  <MarkerIcon
                    lat={data.testDriveLocation.geometry.latitude}
                    lng={data.testDriveLocation.geometry.longitude}
                  />
                </GoogleMap>
              </div>

              <div className="w-full px-4 md:px-0">
                <hr className="hr-thick mt-10" />
              </div>
            </section>
          ) : (
            ""
          )}

          {data?.status !== "sold" && (
            <div ref={loanCalculatorRef} className="mt-4">
              <LoanCalculator
                loanable={isLoanable}
                initialVehiclePrice={data.Price}
                onMonthlyChange={(total: number) => {
                  setMonthlyPayment(total);
                }}
                onApplyClick={() => {
                  setSource("Apply for a loan");
                  setRedirectUrl("/account/borrow");
                  setIsSignup(true, { redirectUrl: "/account/borrow" });
                }}
              />
            </div>
          )}

          {data?.status != "sold" && (
            <OtherVehiclesSection
              state={data.listingLocation?.state}
              type={data.CarMake}
              otherVehicleData={vehicleData.data}
              similarData={similarData.data}
            />
          )}
          {data?.status != "sold" && (
            <VehicleTransactionInfoSection
              state={data.listingLocation?.state}
              shortname={data.listingLocation?.stateShortname}
            />
          )}
          {extraDataForSold != null && (
            <div className="mt-6">
              <ContentBlock
                data={extraDataForSold[0]}
                allData={extraDataForSold}
              />
            </div>
          )}
          {data?.status == "sold" && <div className=""></div>}
          {extraDataForSold != null && <LinkHub data={extraDataForSold[1]} />}

          <section className="w-[85%] px-1 pt-[50px] sm:pt-[77px] text-sm text-center text-[#212529] font-medium opacity-80 pb-8 mx-auto">
            {disclaimer}
          </section>
        </main>
      )}
    </>
  );
};

export const DetailGetPaths: GetStaticPaths = async () => {
  const buildEmpty = !paServerInventoryBuild ?? false;
  console.log(
    `Building${buildEmpty ? " empty - no inventory" : " with inventory"}`
  );

  if (!buildEmpty) {
    try {
      const { getTotalData } = serverApi();

      const content = (await getTotalData().catch((e) => {
        console.log(`GetStaticPaths: `, e.message);
      })) || { data: [] };

      const paths = (content?.data || []).map((item: any) => {
        return {
          params: {
            slug: item.slug,
          },
        };
      });

      return {
        paths,
        fallback: "blocking",
      };
    } catch (e: any) {
      console.log(`GetStaticPaths: `, e.message);
    }
  }

  return {
    paths: [],
    fallback: "blocking",
  };
};

export async function DetailGetProps(context: any): Promise<{
  props: Props;
}> {
  let content: any = "Not found.";
  let similarVehicleData = [];
  let vehicleData: any = {};
  let pageSnippets: any = {};
  let extraDataForSold: any = null;
  let banner: any = {};
  const { getDataBySlug, getPageData } = serverApi();
  try {
    // let slug: string | undefined; //  | string[] | undefined;
    if (context?.params?.slug !== undefined) {
      const slugs = Array.isArray(context.params.slug)
        ? context.params.slug
        : [context.params.slug];
      const slug = slugs[0];
      //slugs.forEach((slug) => async {
      content = await getDataBySlug(slug);
      content = content.data;

      if (content?.userId?.createdAt) {
        let date = new Date(content.userId.createdAt);
        let arr = date.toString().split(" ");
        let date_str = arr[1] + " " + arr[2] + ", " + arr[3];
        content.userId.createdAt = date_str;
      }
      (content?.uploadImages || []).map((item: any) => {
        const image_url = item.images;
        if (item?.images && item.images.includes("vehicle-listing")) {
          item.images = image_url.substring(
            image_url.indexOf("/vehicle-listing"),
            image_url.length
          );
        } else if (item.images.includes("jfif")) {
          item.images = item.images;
        } else {
          item.images = image_url.substring(
            image_url.indexOf("/listings"),
            image_url.length
          );
        }
      });
    }

    if (content?.status == "sold") {
      extraDataForSold = LISTING_DETAILS_SOLD_CONTENT;
    }
    pageSnippets = await getSnippets();
    banner = await getBanner();
    let excludeSlugs = [content?.slug];

    vehicleData = await getPageData(
      6,
      LISTING_VEHICLES_LIMIT,
      0,
      "All Vehicles",
      "",
      "",
      [],
      [],
      MIN_PRICE,
      MAX_PRICE,
      MIN_YEAR,
      MAX_YEAR,
      MIN_MILES,
      MAX_MILES,
      "",
      initFilters,
      "",
      content?.listingLocation?.state,
      0,
      0,
      50,
      "Newest",
      excludeSlugs
    );
    const newSlugs = vehicleData.data.map((item: any) => item.slug);
    excludeSlugs = [...excludeSlugs, ...newSlugs];

    let modelsArr: Array<string> = [];
    modelsArr.push(content?.CarModel);
    similarVehicleData = await getPageData(
      6,
      LISTING_VEHICLES_LIMIT,
      0,
      "All Vehicles",
      "",
      content?.CarMake,
      [],
      [],
      MIN_PRICE,
      MAX_PRICE,
      MIN_YEAR,
      MAX_YEAR,
      MIN_MILES,
      MAX_MILES,
      "",
      initFilters,
      "",
      "",
      0,
      0,
      50,
      "Newest",
      excludeSlugs
    );
  } catch (e: any) {
    console.log(`GetStaticPaths Failed: ${e.message}`);
  }
  return {
    props: {
      content: content || null,
      vehicleData,
      similarData: similarVehicleData,
      headers: pageSnippets.header ?? {},
      banner: banner,
      extraDataForSold,
    },
  };
}

export default ListingPage;
